import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

/** Admin — add or edit any user's hours on any job for any day. */
export const HoursEditorDialog = ({ api, open, onOpenChange, employees, projects, initial, onSaved }) => {
  const [form, setForm] = useState({ user_id: '', project_id: '', date: '', hours: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ user_id: initial?.user_id || '', project_id: initial?.project_id || '', date: initial?.date || format(new Date(), 'yyyy-MM-dd'), hours: initial?.hours ?? '' });
  }, [open, initial]);

  const save = async () => {
    const hours = parseFloat(form.hours);
    if (!form.user_id || !form.project_id || !form.date) { toast.error('Pick a person, a job and a date'); return; }
    if (!(hours > 0)) { toast.error('Enter the hours worked'); return; }
    setSaving(true);
    try {
      await api.put('/admin/hours', { ...form, hours });
      toast.success('Hours saved');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save hours');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2A3240] border-white/10 text-white sm:max-w-md" data-testid="hours-editor-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? 'Edit hours' : 'Add hours'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-gray-300">Person</Label>
            <Select value={form.user_id} onValueChange={v => setForm(f => ({ ...f, user_id: v }))} disabled={!!initial?.id}>
              <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="hours-editor-user"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
                {(employees || []).map(e => <SelectItem key={e.id} value={e.id} className="text-white focus:bg-[#F5A623]/20">{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-gray-300">Job</Label>
            <Select value={form.project_id} onValueChange={v => setForm(f => ({ ...f, project_id: v }))} disabled={!!initial?.id}>
              <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="hours-editor-project"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
                {(projects || []).map(p => <SelectItem key={p.id} value={p.id} className="text-white focus:bg-[#F5A623]/20">{p.project_ref}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-gray-300">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} disabled={!!initial?.id} className="bg-[#1E2530] border-white/20 text-white" data-testid="hours-editor-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-300">Hours</Label>
              <Input type="number" step="0.25" min="0.25" max="24" inputMode="decimal" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} className="bg-[#1E2530] border-white/20 text-white" data-testid="hours-editor-hours" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-white">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="hours-editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save hours'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
